import React, { useCallback, useEffect, useState } from "react";
import { FileText, ExternalLink, ChevronRight, Maximize2, MoreHorizontal, X, Minus, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { getAppointmentDocument } from "@/lib/telehealth";
import { prettyType, labFlagKind } from "@/components/patientdocs/pdUtil";
import LensCard from "@/components/patientdocs/pdLens";
import { firstName } from "./thModel";
import { isDocNew, newDocCount, useSeenVersion } from "./thSeen";

// Patient uploads shown with their Lens analysis. The analysis comes embedded in
// GET /televisit/appointments/{id}/documents (documents[].extraction); the original
// file is fetched per row from GET /appointments/{id}/document/{doc_id}.
const FAINT = "#9B9B97", GREY2 = "#6C6C69", HAIR = "#EFEFF1", INDIGO = "#5856D6", INK = "#141412", AMBER = "#9A5A00";
const isPdf = (ct) => String(ct || "").includes("pdf");
const fmtTime = (v) => (v ? new Date(v).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const fmtSize = (b) => { const n = Number(b); if (!b || isNaN(n)) return ""; if (n < 1024) return `${n} B`; if (n < 1048576) return `${Math.round(n / 1024)} KB`; return `${(n / 1048576).toFixed(1)} MB`; };
const arrowOf = (f) => { const s = String(f || "").toLowerCase(); if (/high|^h|hh/.test(s)) return " ↑"; if (/low|^l|ll/.test(s)) return " ↓"; return ""; };
const labResults = (ex) => (Array.isArray(ex?.results) ? ex.results : Array.isArray(ex?.lab_results) ? ex.lab_results : Array.isArray(ex?.tests) ? ex.tests : Array.isArray(ex?.result_items) ? ex.result_items : []);
const confPct = (d) => { const c = d.extraction?.extraction_confidence || {}; const v = c.confidence ?? c.score; const n = Number(v); if (v == null || isNaN(n)) return null; return n <= 1 ? Math.round(n * 100) : Math.round(n); };

// Line 1 for a row: ink headline + amber chips only for flagged values.
function rowSummary(d) {
  const ex = d.extraction || {}, type = d.document_type;
  const rs = labResults(ex);
  if (type === "lab_result" || rs.length) {
    const flagged = rs.filter((r) => labFlagKind(r.flag));
    const headline = `${rs.length} result${rs.length === 1 ? "" : "s"}${flagged.length ? ` · ${flagged.length} out of range` : " · all in range"}`;
    const chips = flagged.slice(0, 3).map((r) => ({ text: `${r.test || r.name || r.test_name || "—"} ${r.value ?? r.result ?? ""}${arrowOf(r.flag)}`.trim(), amber: true }));
    const inRange = rs.length - flagged.length;
    if (inRange > 0 && chips.length < 3) chips.push({ text: `+${inRange} in range`, amber: false });
    return { headline, chips };
  }
  if (type === "medication_summary" || type === "medication_list" || Array.isArray(ex.medications)) {
    const meds = Array.isArray(ex.medications) ? ex.medications : [];
    const act = meds.filter((m) => m.status !== "discontinued").length;
    return { headline: `${act} active medication${act === 1 ? "" : "s"}`, chips: [] };
  }
  const readings = Array.isArray(ex.readings) ? ex.readings : Array.isArray(ex.home_readings) ? ex.home_readings : null;
  if (readings) return { headline: `${readings.length} reading${readings.length === 1 ? "" : "s"}`, chips: [] };
  return { headline: (type && prettyType(type)) || d.filename || "Document", chips: [] };
}

export const openFile = async (apptId, docId) => {
  try {
    const x = await getAppointmentDocument(apptId, docId);
    const u = x?.url || x?.signed_url;
    if (u) { window.open(u, "_blank", "noopener"); return; }
    if (!x?.base64 && !x?.file_content) { toast("This file has no preview."); return; }
    const bin = atob(x.base64 || x.file_content), arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    window.open(URL.createObjectURL(new Blob([arr], { type: x.content_type || "application/octet-stream" })), "_blank", "noopener");
  } catch { toast.error("Couldn't open the document."); }
};

function Thumb({ fd, w = 32, h = 40, radius = 4, shadow }) {
  const box = { width: w, height: h, borderRadius: radius, border: shadow ? 0 : `1px solid ${HAIR}`, boxShadow: shadow ? "0 2px 10px rgba(20,20,18,.14)" : "none", overflow: "hidden", flex: "none", background: "#F1F1F4", display: "grid", placeItems: "center" };
  if (!fd || fd.loading) return <div style={box}><Loader2 size={Math.min(14, w / 2)} className="animate-spin" style={{ color: FAINT }} /></div>;
  if (fd.error) return <div style={box}><FileText size={Math.min(18, w / 2)} style={{ color: FAINT }} /></div>;
  const src = fd.url || fd.dataUrl;
  if (isPdf(fd.content_type)) {
    const iw = w / 0.4, ih = h / 0.4;
    return <div style={box}><div style={{ width: w, height: h, overflow: "hidden", pointerEvents: "none" }}><iframe title="doc-thumb" src={`${src}#toolbar=0&view=FitH&page=1`} style={{ width: iw, height: ih, border: 0, transform: "scale(0.4)", transformOrigin: "top left" }} /></div></div>;
  }
  return <div style={box}><img alt="" src={src} style={{ width: "100%", height: "100%", objectFit: "cover" }} /></div>;
}

function Inline({ a, d, fd, onFull, onOpenFile, onDownload, onClose }) {
  const id = d.doc_id || d.id, src = fd?.url || fd?.dataUrl, conf = confPct(d);
  return (
    <div className="pb-3" data-testid={`th-docx-inline-${id}`}>
      <div className="flex items-center gap-2 px-3 py-2 rounded-t-[10px]" style={{ background: "#FAFAFC", border: `1px solid ${HAIR}` }}>
        <FileText size={13} style={{ color: GREY2 }} />
        <span className="text-[12px] font-semibold truncate" style={{ color: INK }}>{d.filename}</span>
        <span className="text-[11.5px] truncate" style={{ color: FAINT }}>{[fmtTime(d.uploaded_at), fmtSize(d.size ?? d.file_size)].filter(Boolean).join(" · ")}</span>
        <span className="flex-1" />
        <button type="button" onClick={() => onFull(id)} className="th-ibtn2" data-testid={`th-docx-full-${id}`}><Maximize2 size={12} />Full view</button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild><button type="button" className="th-ibtn2" aria-label="More" data-testid={`th-docx-more-${id}`}><MoreHorizontal size={14} /></button></DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="z-[90]">
            <DropdownMenuItem onClick={() => onOpenFile(id)} data-testid={`th-docx-openfile-${id}`}>Open file</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onDownload(d)} data-testid={`th-docx-download-${id}`}>Download</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button type="button" onClick={onClose} className="th-ibtn2" aria-label="Close inline" data-testid={`th-docx-close-${id}`}><X size={14} /></button>
      </div>
      <div className="grid gap-3 p-3 grid-cols-1 lg:grid-cols-[180px_1fr] rounded-b-[10px]" style={{ border: `1px solid ${HAIR}`, borderTop: 0, background: "#fff" }}>
        <button type="button" onClick={() => onFull(id)} data-testid={`th-docx-original-${id}`} aria-label="Open full view">
          <Thumb fd={fd} w={180} h={240} radius={8} shadow />
        </button>
        <div className="min-w-0 max-h-[360px] overflow-y-auto pr-1">
          {d.extraction ? <LensCard analysis={d} /> : <div className="text-[12.5px]" style={{ color: FAINT }}>No analysis returned.</div>}
          <div className="text-[11px] mt-2" style={{ color: FAINT }}>Read by Lens{conf ? ` · ${conf}% confident` : ""} · uploaded by the patient through their visit link</div>
        </div>
      </div>
    </div>
  );
}

function FullView({ a, d, fd, onClose, onOpenFile }) {
  const [scale, setScale] = useState(1);
  useEffect(() => { const on = (e) => { if (e.key === "Escape") onClose(); }; window.addEventListener("keydown", on); return () => window.removeEventListener("keydown", on); }, [onClose]);
  const id = d.doc_id || d.id, src = fd?.url || fd?.dataUrl, conf = confPct(d);
  const meta = [d.document_type && prettyType(d.document_type), `Sent by ${firstName(a)} through their visit link`, fmtTime(d.uploaded_at), fmtSize(d.size ?? d.file_size)].filter(Boolean).join(" · ");
  return (
    <div className="fixed inset-0 z-[80] flex" style={{ background: "rgba(20,20,18,.55)", padding: 16 }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid={`th-docx-fullview-${id}`}>
      <div className="flex flex-col w-full h-full rounded-[14px] overflow-hidden" style={{ background: "#fff" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 px-4 py-3 flex-none" style={{ borderBottom: `1px solid ${HAIR}` }}>
          <div className="min-w-0">
            <div className="text-[16px] font-bold truncate" style={{ color: INK }}>{d.filename}</div>
            <div className="text-[11.5px] mt-0.5" style={{ color: FAINT }}>{meta}</div>
          </div>
          <span className="flex-1" />
          <button type="button" onClick={() => onOpenFile(id)} className="th-btn sec" data-testid="th-full-openfile">Open file <ExternalLink size={13} /></button>
          <button type="button" onClick={onClose} className="th-ibtn2" aria-label="Close full view" data-testid="th-full-close"><X size={16} /></button>
        </div>
        <div className="flex-1 grid min-h-0 grid-cols-1 lg:grid-cols-2">
          <div className="min-h-0 flex flex-col" style={{ background: "#F1F1F4", borderRight: `1px solid ${HAIR}` }}>
            <div className="flex items-center gap-2 px-3 py-2 flex-none" style={{ borderBottom: `1px solid ${HAIR}` }}>
              <span className="flex-1" />
              <button type="button" onClick={() => setScale((s) => Math.max(0.5, +(s - 0.25).toFixed(2)))} className="th-ibtn2" aria-label="Zoom out" data-testid="th-full-zoomout"><Minus size={13} /></button>
              <span className="text-[12px] tabular-nums" style={{ color: GREY2, minWidth: 42, textAlign: "center" }}>{Math.round(scale * 100)}%</span>
              <button type="button" onClick={() => setScale((s) => Math.min(3, +(s + 0.25).toFixed(2)))} className="th-ibtn2" aria-label="Zoom in" data-testid="th-full-zoomin"><Plus size={13} /></button>
            </div>
            <div className="flex-1 overflow-auto grid place-items-center p-4">
              {!src ? <div className="text-[12px]" style={{ color: FAINT }}>Couldn't load this document.</div>
                : <div style={{ transform: `scale(${scale})`, transformOrigin: "center top" }}>
                    {isPdf(fd.content_type)
                      ? <iframe title="full-orig" src={src} style={{ width: "min(78vw, 900px)", height: "78vh", border: 0, background: "#fff", borderRadius: 8 }} />
                      : <img alt="original" src={src} style={{ maxWidth: "min(78vw, 900px)", borderRadius: 8, display: "block" }} />}
                  </div>}
            </div>
          </div>
          <div className="min-h-0 overflow-y-auto p-4">
            {d.extraction ? <LensCard analysis={d} /> : <div className="text-[12.5px]" style={{ color: FAINT }}>No analysis returned.</div>}
            <div className="text-[11px] mt-3" style={{ color: FAINT }}>Read by Lens{conf ? ` · ${conf}% confident` : ""} · values as written on the document</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Doc({ a, d, fd, isNew, open, onToggle, onFull, onOpenFile, onDownload }) {
  const id = d.doc_id || d.id;
  const processing = d.status && d.status !== "completed";
  const { headline, chips } = rowSummary(d);
  const line2 = [d.document_type ? prettyType(d.document_type) : "Document", d.filename, fmtTime(d.uploaded_at)].filter(Boolean).join(" · ");
  return (
    <div className={`border-t first:border-t-0 ${isNew ? "th-new-static th-flashin rounded-[8px] -mx-2 px-2" : ""}`} style={{ borderColor: HAIR }}>
      <button type="button" onClick={() => onToggle(id)} className="w-full flex items-center gap-3 py-2.5 text-left" data-testid={`th-docx-${id}`} aria-expanded={open}>
        <Thumb fd={fd} />
        <div className="min-w-0 flex-1">
          {processing
            ? <div className="text-[13px] font-semibold inline-flex items-center gap-1.5" style={{ color: INDIGO }} data-testid={`th-docx-reading-${id}`}><span className="th-dot sm pulse" style={{ background: INDIGO }} />Lens is reading it…</div>
            : <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[13px] font-semibold" style={{ color: INK }}>{headline}</span>
                {chips.map((c, i) => <span key={i} className="text-[11px] px-1.5 py-[1px] rounded-[6px] whitespace-nowrap" style={c.amber ? { background: "rgba(184,116,26,.13)", color: AMBER, fontWeight: 600 } : { background: "#F1F1F4", color: GREY2 }}>{c.text}</span>)}
                {isNew ? <span className="th-ntag ind" data-testid={`th-docx-new-${id}`}>New</span> : null}
              </div>}
          <div className="text-[11.5px] truncate mt-0.5" style={{ color: FAINT }} title={d.filename}>{isNew && !processing ? `New · ${line2}` : line2}</div>
        </div>
        <ChevronRight size={15} className="flex-none" style={{ color: FAINT, transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }} />
      </button>
      {open ? <Inline a={a} d={d} fd={fd} onFull={onFull} onOpenFile={onOpenFile} onDownload={onDownload} onClose={() => onToggle(id)} /> : null}
    </div>
  );
}

export function PatientDocs({ a, docs }) {
  useSeenVersion();
  const [files, setFiles] = useState({});
  const [openId, setOpenId] = useState(null);
  const [fullId, setFullId] = useState(null);

  const loadFile = useCallback(async (docId) => {
    setFiles((f) => ({ ...f, [docId]: { loading: true } }));
    try {
      const x = await getAppointmentDocument(a.appointment_id, docId);
      const url = x?.url || x?.signed_url;
      if (url) { setFiles((p) => ({ ...p, [docId]: { url, content_type: x.content_type } })); return; }
      const b64 = x?.base64 || x?.file_content;
      if (b64) { setFiles((p) => ({ ...p, [docId]: { dataUrl: `data:${x.content_type || "application/octet-stream"};base64,${b64}`, content_type: x.content_type } })); return; }
      setFiles((p) => ({ ...p, [docId]: { error: true } }));
    } catch { setFiles((p) => ({ ...p, [docId]: { error: true } })); }
  }, [a.appointment_id]);

  // Thumbnails for every row (one original fetch per document, cached).
  useEffect(() => { docs.forEach((d) => { const id = d.doc_id || d.id; if (id && !files[id]) loadFile(id); }); }, [docs]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!docs.length) return null;
  const nNew = newDocCount(a);
  const onToggle = (id) => { setOpenId((cur) => (cur === id ? null : id)); if (!files[id]) loadFile(id); };
  const onFull = (id) => { setFullId(id); if (!files[id]) loadFile(id); };
  const onOpenFile = (id) => openFile(a.appointment_id, id);
  const onDownload = (d) => { const id = d.doc_id || d.id, fd = files[id], href = fd?.url || fd?.dataUrl; if (!href) { toast("File still loading."); return; } const el = document.createElement("a"); el.href = href; el.download = d.filename || "document"; el.click(); };
  const fullDoc = docs.find((d) => (d.doc_id || d.id) === fullId);

  return (
    <div className="th-card mt-3" data-testid="th-docs-panel">
      <div className="flex items-center gap-2"><FileText size={14} /><span className="text-[13px] font-semibold">Documents from the patient</span>{nNew > 0 ? <span className="th-ntag ind" data-testid="th-docs-new">New</span> : null}<span className="text-[12px]" style={{ color: FAINT }}>{docs.length} · with what Lens read</span></div>
      {docs.map((d) => { const id = d.doc_id || d.id; return <Doc key={id} a={a} d={d} fd={files[id]} isNew={isDocNew(a, id)} open={openId === id} onToggle={onToggle} onFull={onFull} onOpenFile={onOpenFile} onDownload={onDownload} />; })}
      {fullDoc ? <FullView a={a} d={fullDoc} fd={files[fullId]} onClose={() => setFullId(null)} onOpenFile={onOpenFile} /> : null}
    </div>
  );
}
